/**
 * BulkDecreeModal — DEKRETACJA WSADOWA (PHASE 25 Faza 2). Modal wywoływany akcją zbiorczą
 * „Dekretuj" na liście faktur (zakupu/sprzedaży). Wspólna REGUŁA dla całej partii —
 * procedura SQL ALBO szablon dekretu — liczona i zapisywana PER dokument po stronie serwera
 * (`POST /api/documents/decree/bulk`).
 *
 * UX wyboru reguły (poprawka 2026-09-14): JEDNA wspólna lista aktywnych reguł firmy+klasy,
 * a typ (Szablon / Procedura) to TAG przy nazwie — nie osobna zakładka. Poprzednio modal
 * startował na „Procedura" i pokazywał „brak aktywnych reguł", gdy tenant miał tylko szablony
 * (fałszywe wrażenie, że dekretacja nie działa). Teraz user wybiera regułę po nazwie, nie
 * musząc z góry wiedzieć, jakiego jest typu; typ i tak wyznacza `source` w payloadzie.
 *
 * Bramka „jedna firma": procedury i szablony są per firma, więc partia musi obejmować jedną
 * firmę. Gdy zaznaczenie ma faktury z wielu firm — modal blokuje wybór reguły i prosi o
 * zawężenie do jednej firmy (lustro bramki 400 MULTIPLE_COMPANIES w backendzie).
 *
 * Istniejące dekrety są ZAWSZE nadpisywane (decyzja właścicielki) — komunikat informuje o tym
 * wprost. Wynik = raport best-effort: ile zadekretowano, ile pominięto i dlaczego.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Code, FileText, Plus } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

export type BulkDecreeDocumentClass = 'purchase_invoice' | 'sale_invoice';

type DecreeSource = 'procedure' | 'template';

interface RuleSummary {
  id: string;
  name: string;
}

/** Reguła w zunifikowanej liście — nazwa + z jakiego katalogu pochodzi (steruje `source`). */
interface UnifiedRule {
  id: string;
  name: string;
  source: DecreeSource;
}

interface BulkDecreeResult {
  decreed: string[];
  skipped: Array<{ id: string; reason: string }>;
  total: number;
}

export interface BulkDecreeModalProps {
  open: boolean;
  onClose: () => void;
  /** Zaznaczone faktury. */
  documentIds: string[];
  documentClass: BulkDecreeDocumentClass;
  /** Zbiór unikalnych firm zaznaczonych faktur — bramka „jedna firma". */
  companyIds: string[];
  /** Po udanym zapisie: odśwież listę + wyczyść zaznaczenie. */
  onDone: () => void;
  testId?: string;
}

function errorCode(err: unknown): string | undefined {
  if (isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    const body = err.response.data as Record<string, unknown>;
    return typeof body.error === 'string' ? body.error : undefined;
  }
  return undefined;
}

export function BulkDecreeModal({
  open,
  onClose,
  documentIds,
  documentClass,
  companyIds,
  onDone,
  testId = 'bulk-decree-modal',
}: BulkDecreeModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<UnifiedRule | null>(null);
  const [result, setResult] = useState<BulkDecreeResult | null>(null);

  const companyId = companyIds.length === 1 ? companyIds[0] : null;
  const multipleCompanies = companyIds.length > 1;

  // Reset stanu przy każdym otwarciu / zmianie zaznaczenia.
  useEffect(() => {
    if (open) {
      setSelected(null);
      setResult(null);
    }
  }, [open, documentIds]);

  // JEDNA lista reguł: szablony + procedury firmy+klasy w jednym zapytaniu. Szablony przed
  // procedurami (typowo tenant ma głównie szablony); w środku grup zachowujemy kolejność z API.
  const rulesQ = useQuery({
    queryKey: ['bulk-decree-rules', companyId, documentClass],
    queryFn: async (): Promise<UnifiedRule[]> => {
      const params = new URLSearchParams({ documentClass, active: 'true' });
      if (companyId) params.set('companyId', companyId);
      const qs = params.toString();
      const [tplRes, procRes] = await Promise.all([
        api.get<{ templates?: RuleSummary[] }>(`/decree-templates?${qs}`),
        api.get<{ procedures?: RuleSummary[] }>(`/decree-procedures?${qs}`),
      ]);
      const templates = (tplRes.data.templates ?? []).map(
        (r): UnifiedRule => ({ id: r.id, name: r.name, source: 'template' }),
      );
      const procedures = (procRes.data.procedures ?? []).map(
        (r): UnifiedRule => ({ id: r.id, name: r.name, source: 'procedure' }),
      );
      return [...templates, ...procedures];
    },
    enabled: open && !!companyId,
    staleTime: 30_000,
  });
  const rules = useMemo(() => rulesQ.data ?? [], [rulesQ.data]);

  const decreeMutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('NO_RULE');
      const res = await api.post<BulkDecreeResult>('/documents/decree/bulk', {
        documentIds,
        source: selected.source,
        ruleId: selected.id,
      });
      return res.data;
    },
    onSuccess: (data) => {
      setResult(data);
      // Czyść zaznaczenie + odśwież listę TYLKO gdy cokolwiek zadekretowano — przy zerowym
      // sukcesie zostawiamy zaznaczenie, żeby dało się poprawić i spróbować ponownie.
      if (data.decreed.length > 0) onDone();
      const failed = data.skipped.length;
      toast.show({
        variant: failed > 0 ? 'error' : 'success',
        title:
          failed > 0
            ? t('tenant.bulkDecree.toast.partial', {
                ok: data.decreed.length,
                failed,
              })
            : t('tenant.bulkDecree.toast.done', { count: data.decreed.length }),
      });
    },
    onError: (err) => {
      const code = errorCode(err);
      if (code === 'MULTIPLE_COMPANIES') {
        toast.show({ variant: 'error', title: t('tenant.bulkDecree.multipleCompanies') });
        return;
      }
      if (code === 'RULE_NOT_FOUND' || code === 'RULE_INACTIVE') {
        toast.show({ variant: 'error', title: t('tenant.bulkDecree.ruleUnavailable') });
        return;
      }
      if (code === 'PERMISSION_DENIED') {
        toast.show({ variant: 'error', title: t('tenant.bulkDecree.permissionDenied') });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.bulkDecree.toast.genericError') });
    },
  });

  const canSubmit = !!companyId && !!selected && !decreeMutation.isPending && !result;

  const reasonLabels = useMemo(
    () => ({
      NOT_FOUND: t('tenant.bulkDecree.reason.notFound'),
      MISSING_COMPANY: t('tenant.bulkDecree.reason.missingCompany'),
      UNSUPPORTED_CLASS: t('tenant.bulkDecree.reason.unsupportedClass'),
      CLASS_MISMATCH: t('tenant.bulkDecree.reason.classMismatch'),
      ACCOUNT_INVALID: t('tenant.bulkDecree.reason.accountInvalid'),
      AMOUNT_MISSING: t('tenant.bulkDecree.reason.amountMissing'),
      EXECUTION_FAILED: t('tenant.bulkDecree.reason.executionFailed'),
      // Na fakturze trwa zadanie przycisku SQL — dekret nie może jej zmienić do końca zadania.
      RECORD_LOCKED_BY_RUN: t('tenant.bulkDecree.reason.recordLocked'),
    }),
    [t],
  );

  const goAddRule = (path: string) => {
    onClose();
    navigate(path);
  };

  const footer =
    result || multipleCompanies ? (
      <div className="flex justify-end">
        <Button variant="secondary" onClick={onClose} data-testid={`${testId}-close-btn`}>
          {t('common.close')}
        </Button>
      </div>
    ) : (
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} data-testid={`${testId}-cancel-btn`}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => decreeMutation.mutate()}
          disabled={!canSubmit}
          loading={decreeMutation.isPending}
          data-testid={`${testId}-submit-btn`}
        >
          {t('tenant.bulkDecree.submit', { count: documentIds.length })}
        </Button>
      </div>
    );

  return (
    <Modal open={open} onClose={onClose} title={t('tenant.bulkDecree.title')} footer={footer}>
      {multipleCompanies ? (
        <div
          className="rounded-md border border-warning-border bg-warning-bg p-3 text-sm text-warning-text"
          data-testid={`${testId}-multiple-companies`}
        >
          {t('tenant.bulkDecree.multipleCompanies')}
        </div>
      ) : result ? (
        <div className="flex flex-col gap-3" data-testid={`${testId}-result`}>
          <p className="text-sm text-text-primary">
            {t('tenant.bulkDecree.result.summary', {
              ok: result.decreed.length,
              total: result.total,
            })}
          </p>
          {result.skipped.length > 0 && (
            <div className="rounded-md border border-border bg-bg-secondary p-3">
              <p className="mb-2 text-xs font-medium uppercase text-text-tertiary">
                {t('tenant.bulkDecree.result.skippedHeader', {
                  count: result.skipped.length,
                })}
              </p>
              <ul className="flex flex-col gap-1 text-sm text-text-secondary">
                {result.skipped.map((s) => (
                  <li key={s.id} className="flex justify-between gap-2">
                    <span className="truncate font-mono text-xs">{s.id.slice(0, 8)}</span>
                    <span>{reasonLabels[s.reason as keyof typeof reasonLabels] ?? s.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-text-secondary">
              {t('tenant.bulkDecree.ruleLabel')}
            </span>

            {rulesQ.isLoading ? (
              <p className="px-1 py-3 text-sm text-text-tertiary">{t('common.loading')}</p>
            ) : rules.length === 0 ? (
              // Pusty stan z akcją zamiast martwego dropdownu — user wie, co zrobić.
              <div
                className="rounded-lg border border-dashed border-border-primary p-4 text-center"
                data-testid={`${testId}-empty`}
              >
                <p className="text-sm font-medium text-text-primary">
                  {t('tenant.bulkDecree.noRulesTitle')}
                </p>
                <p className="mt-1 text-xs text-text-tertiary">
                  {t('tenant.bulkDecree.noRulesHint')}
                </p>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => goAddRule('/settings/decree-templates')}
                    data-testid={`${testId}-add-template`}
                  >
                    {t('tenant.bulkDecree.addTemplate')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => goAddRule('/settings/decree-procedures')}
                    data-testid={`${testId}-add-procedure`}
                  >
                    {t('tenant.bulkDecree.addProcedure')}
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="flex max-h-64 flex-col gap-1 overflow-auto rounded-lg border border-border-primary p-1"
                role="listbox"
                aria-label={t('tenant.bulkDecree.ruleLabel')}
                data-testid={`${testId}-rule-list`}
              >
                {rules.map((r) => {
                  const isSel = selected?.id === r.id && selected?.source === r.source;
                  const isTpl = r.source === 'template';
                  return (
                    <Button
                      key={`${r.source}-${r.id}`}
                      variant="ghost"
                      role="option"
                      aria-selected={isSel}
                      onClick={() => setSelected(r)}
                      data-testid={`${testId}-rule-${r.id}`}
                      className={`w-full justify-start gap-2.5 rounded-md px-2.5 py-2 ${
                        isSel
                          ? 'bg-accent-50 ring-1 ring-accent-500 hover:bg-accent-50 dark:bg-accent-900/20'
                          : 'hover:bg-bg-secondary'
                      }`}
                      leftIcon={
                        <span
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-bg-tertiary text-text-secondary"
                          aria-hidden="true"
                        >
                          {isTpl ? (
                            <FileText className="h-3.5 w-3.5" />
                          ) : (
                            <Code className="h-3.5 w-3.5" />
                          )}
                        </span>
                      }
                      rightIcon={
                        <Badge color={isTpl ? 'blue' : 'amber'} size="sm">
                          {isTpl
                            ? t('tenant.bulkDecree.sourceTemplate')
                            : t('tenant.bulkDecree.sourceProcedure')}
                        </Badge>
                      }
                    >
                      <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-text-primary">
                        {r.name}
                      </span>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          <p className="text-sm text-text-tertiary" data-testid={`${testId}-info`}>
            {t('tenant.bulkDecree.info', { count: documentIds.length })}
          </p>
        </div>
      )}
    </Modal>
  );
}
